import { computed, onScopeDispose, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { provideApolloClient, useQuery } from '@vue/apollo-composable';
import { useRouter } from 'vue-router';

import apolloClient from '@/apollo/client';
import {
  UploadAbortDocument,
  UploadAuthorizeDocument,
  UploadCompleteDocument,
  UploadsDocument,
  UploadType,
  UploadUpdateDocument,
} from '@/gql/graphql';
import { POLLING_INTERVAL_LONG, POLLING_INTERVAL_SHORT } from '@/constants';
import { useUserStore } from '@/store/user';
import {
  BrowserUploadApi,
  BrowserUploadProgress,
  cancelBrowserUpload,
  isAbortError,
  runBrowserUpload,
  uploadErrorMessage,
} from '@/utils/browser-upload';
import { STALE_UPLOAD_THRESHOLD, UPLOAD_STATUS } from '@/utils/upload-utils';

export interface ActiveUpload extends UploadType {
  file: File;
  abortController: AbortController;
}

export const useUploadsStore = defineStore('upload', () => {
  const uploads = ref<UploadType[]>([]);
  const activeUploads = ref(new Map<number, ActiveUpload>());
  const uploadErrors = ref(new Map<number, string>());
  const router = useRouter();
  const pollingInterval = ref(POLLING_INTERVAL_LONG);
  const userStore = useUserStore();

  provideApolloClient(apolloClient);

  const browserUploadApi: BrowserUploadApi = {
    async authorize(id, partNumbers) {
      const response = await apolloClient.mutate({
        mutation: UploadAuthorizeDocument,
        variables: { id, partNumbers },
      });
      const authorization = response.data?.uploadAuthorize;
      if (!authorization) {
        throw new Error('The API did not return upload authorization.');
      }
      return authorization;
    },
    async update(input) {
      const response = await apolloClient.mutate({
        mutation: UploadUpdateDocument,
        variables: input,
      });
      const updated = response.data?.uploadUpdate;
      if (!updated) throw new Error('The API did not update upload progress.');
      return updated;
    },
    async complete(id) {
      const response = await apolloClient.mutate({
        mutation: UploadCompleteDocument,
        variables: { id },
      });
      const completed = response.data?.uploadComplete;
      if (!completed) throw new Error('The API did not complete the upload.');
      return completed;
    },
    async abort(id) {
      const response = await apolloClient.mutate({
        mutation: UploadAbortDocument,
        variables: { id },
      });
      const aborted = response.data?.uploadAbort;
      if (!aborted) throw new Error('The API did not abort the upload.');
      return aborted;
    },
  };

  watch(
    () => router.currentRoute.value.name,
    (newName) => {
      pollingInterval.value =
        newName === 'UploadManager'
          ? POLLING_INTERVAL_SHORT
          : POLLING_INTERVAL_LONG;
    },
    { immediate: true }
  );

  function updateLocalProgress(
    activeUpload: ActiveUpload,
    progress: BrowserUploadProgress
  ) {
    activeUpload.progress =
      progress.total === 0
        ? progress.done
          ? 100
          : 99
        : progress.done
        ? 100
        : Math.min(99, Math.round((progress.uploaded / progress.total) * 100));
    activeUpload.status = progress.done
      ? UPLOAD_STATUS.COMPLETED
      : UPLOAD_STATUS.RUNNING;
    const existing = uploads.value.find(
      (candidate) => candidate.id === activeUpload.id
    );
    if (existing) {
      existing.progress = activeUpload.progress;
      existing.status = activeUpload.status;
      existing.sessionUri = activeUpload.sessionUri;
    }
  }

  async function resumeUpload(upload: UploadType, file: File): Promise<void> {
    if (!upload.id || !upload.uploadStrategy) {
      throw new Error('Upload ID or strategy is missing.');
    }
    if (activeUploads.value.has(upload.id)) {
      throw new Error(`Upload ${upload.id} is already active.`);
    }

    const activeUpload: ActiveUpload = {
      ...upload,
      file,
      abortController: new AbortController(),
    };
    activeUploads.value.set(upload.id, activeUpload);
    uploadErrors.value.delete(upload.id);

    const existingIndex = uploads.value.findIndex(
      (candidate) => candidate.id === upload.id
    );
    if (existingIndex < 0) {
      uploads.value.unshift(activeUpload);
    }

    try {
      const completed = await runBrowserUpload({
        upload: activeUpload,
        file,
        api: browserUploadApi,
        signal: activeUpload.abortController.signal,
        onProgress: (progress) => updateLocalProgress(activeUpload, progress),
      });
      Object.assign(activeUpload, completed);
      uploadErrors.value.delete(upload.id);
    } catch (error) {
      if (
        !isAbortError(error) &&
        !activeUpload.abortController.signal.aborted
      ) {
        const message = uploadErrorMessage(error);
        uploadErrors.value.set(upload.id, message);
        activeUpload.status = UPLOAD_STATUS.STALE;
        try {
          await browserUploadApi.update({
            id: upload.id,
            sessionUri: activeUpload.sessionUri || undefined,
            status: UPLOAD_STATUS.STALE,
            progress: activeUpload.progress,
          });
        } catch (updateError) {
          console.error(
            'Could not persist failed upload state:',
            updateError instanceof Error ? updateError.name : 'unknown error'
          );
        }
      }
    } finally {
      activeUploads.value.delete(upload.id);
      await refetch()?.catch((error) => {
        console.error(
          'Could not refresh uploads after transfer:',
          error instanceof Error ? error.name : 'unknown error'
        );
      });
    }
  }

  async function abortUpload(uploadId: number): Promise<void> {
    const activeUpload = activeUploads.value.get(uploadId);
    const upload =
      activeUpload ||
      uploads.value.find((candidate) => candidate.id === uploadId);
    if (!upload) throw new Error(`No upload found with id ${uploadId}.`);

    activeUpload?.abortController.abort();
    try {
      const aborted = await cancelBrowserUpload({
        upload,
        api: browserUploadApi,
      });
      Object.assign(upload, aborted);
      uploadErrors.value.delete(uploadId);
    } catch (error) {
      uploadErrors.value.set(uploadId, uploadErrorMessage(error));
      throw error;
    } finally {
      activeUploads.value.delete(uploadId);
      await refetch()?.catch((error) => {
        console.error(
          'Could not refresh uploads after abort:',
          error instanceof Error ? error.name : 'unknown error'
        );
      });
    }
  }

  function uploadError(uploadId: number | null | undefined): string | null {
    return uploadId ? uploadErrors.value.get(uploadId) || null : null;
  }

  const {
    loading,
    error,
    onResult: onUploadsQueryResult,
    onError: onUploadsQueryError,
    refetch,
  } = useQuery(
    UploadsDocument,
    () => ({}),
    () => ({
      enabled: computed(() => userStore.authenticated),
      fetchPolicy: 'network-only',
    })
  );

  onUploadsQueryResult((result) => {
    if (result.data?.uploads) {
      setUploads(
        result.data.uploads.filter(
          (upload): upload is UploadType => upload !== null
        )
      );
    }
  });

  onUploadsQueryError((queryError) => {
    console.error('Error fetching uploads:', queryError.message);
  });

  function setUploads(fetchedUploads: UploadType[]) {
    uploads.value = fetchedUploads.map((upload) => {
      const active = upload.id ? activeUploads.value.get(upload.id) : undefined;
      return {
        ...upload,
        status: active?.status || uploadStatus(upload),
        progress: active?.progress ?? upload.progress,
        sessionUri: active?.sessionUri ?? upload.sessionUri,
        createdAt: upload.createdAt
          ? new Date(upload.createdAt)
          : upload.createdAt,
        updatedAt: upload.updatedAt
          ? new Date(upload.updatedAt)
          : upload.updatedAt,
      };
    });
  }

  const hasStaleUploads = computed(() =>
    uploads.value.some((upload) => upload.status === UPLOAD_STATUS.STALE)
  );

  function uploadStatus(upload: UploadType) {
    if (
      (upload.status === UPLOAD_STATUS.RUNNING ||
        upload.status === UPLOAD_STATUS.PENDING) &&
      upload.updatedAt &&
      upload.id &&
      !activeUploads.value.has(upload.id) &&
      new Date(upload.updatedAt).getTime() < Date.now() - STALE_UPLOAD_THRESHOLD
    ) {
      return UPLOAD_STATUS.STALE;
    }
    return upload.status;
  }

  let timer: number | undefined;
  function startPolling() {
    stopPolling();
    if (!userStore.authenticated || document.hidden) return;
    timer = window.setInterval(() => {
      if (userStore.authenticated && !document.hidden) void refetch();
    }, pollingInterval.value);
  }

  function stopPolling() {
    if (timer !== undefined) clearInterval(timer);
    timer = undefined;
  }

  function visibility() {
    startPolling();
    if (userStore.authenticated && !document.hidden) void refetch();
  }
  document.addEventListener('visibilitychange', visibility);
  watch([pollingInterval, () => userStore.authenticated], startPolling, {
    immediate: true,
  });
  onScopeDispose(() => {
    stopPolling();
    document.removeEventListener('visibilitychange', visibility);
  });

  return {
    uploads,
    loading,
    error,
    pollingInterval,
    hasStaleUploads,
    resumeUpload,
    abortUpload,
    uploadError,
  };
});
